import { inject, Injectable, OnDestroy } from '@angular/core';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { CucmHuntList, JobState, JobStatus } from '../../../shared/models/generated/ziroModels';
import { MigrationsCucmHuntListsPollingContext } from './migrations-cucm-hunt-lists-polling.context';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter, map, take, tap } from 'rxjs/operators';

@Injectable()
export class MigrationsCucmHuntListsContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _migrationsCucmHuntListsPollingContext = inject(MigrationsCucmHuntListsPollingContext);

  private _stateSource = new ReplaySubject<CucmHuntList[]>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/hunt-list-report`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._migrationsCucmHuntListsPollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  private _init(): Observable<void> {
    this._migrationsCucmHuntListsPollingContext.startPolling();
    return this._migrationsCucmHuntListsPollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._migrationsCucmHuntListsPollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of([]);
      }),
      filter((report) => !!report),
      map((report: CucmHuntList[]) => {
        this._stateSource.next(report);
      }),
      take(1),
    );
  }

  private _get(): Observable<CucmHuntList[]> {
    return this._http.get<CucmHuntList[]>(this._baseUrl);
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null);
  }
}
